import React from 'react';
import { convictionLabel } from '../../utils/convictionLabel';
import CompanyName from '../CompanyName';
import TickerLogo from '../common/TickerLogo';
import PortfolioExpandedRow from './PortfolioExpandedRow';
import WatchButton from '../WatchButton';

/**
 * PortfolioRow - Single table row for each position.
 * Shows symbol, shares, prices, P&L, pillar scores, single conviction score, and action.
 * When expanded, shows the PortfolioExpandedRow detail panel with guidance and test breakdowns.
 *
 * 2026-09-11: the 🔔 Grade Watch bell now sits in the symbol cell. It used to
 * live only on Screener results, which assumed you discover tickers by screening
 * — but your own holdings are the most natural thing to watch, and plenty of
 * users never run their portfolio through the Screener. WatchButton renders
 * nothing for logged-out users and enforces the tier cap server-side.
 */
const PortfolioRow = ({
  item,
  score,
  isExpanded,
  toggleRow,
  getScoreColor,
  getBandColor,
  formatCurrency,
  formatScore,
  formatPercent,
  setChartSymbol,
  selectedTests,
  handleTestClick,
  showEnergyBreakdown,
  toggleEnergyBreakdown,
  index,
  turbulence,
}) => {
  const isCrypto = score?.is_crypto || score?.data_quality?.is_crypto;
  const isETF = score?.is_etf || score?.data_quality?.is_etf;
  const isMutualFund = score?.is_mutual_fund || score?.data_quality?.is_mutual_fund;

  // Single conviction score — check both flat and nested paths for compatibility
  const conv = score?.conviction || {};
  const conviction = conv.combined_conviction ?? score?.combined_conviction ?? 0;
  const action = conv.recommended_action ?? score?.recommended_action ?? score?.long_term_action ?? '-';
  const guidance = conv.guidance ?? score?.guidance ?? '';

  // Crypto gets sentiment labels instead of actions
  // Coins have real Quality/Convex since 2026-11-02, so they get the same five
  // labels as stocks (was BULLISH / NEUTRAL / BEARISH at 65/45). The class
  // keeps the existing three colors.
  const getCryptoSentiment = (conv) => {
    const className = conv >= 72 ? 'bullish' : conv >= 48 ? 'neutral' : 'bearish';
    return { label: convictionLabel(conv).toUpperCase(), className };
  };

  const cryptoSentiment = isCrypto ? getCryptoSentiment(conviction) : null;

  // Conviction band and color
  const band = conv.conviction_band ?? score?.conviction_band ?? (conviction >= 78 ? 'High' : conviction >= 72 ? 'High-Medium' : conviction >= 57 ? 'Medium' : conviction >= 48 ? 'Medium-Low' : 'Low');
  const bandColor = getBandColor(band);

  return (
    <React.Fragment>
      <tr className={`position-row ${isExpanded ? 'expanded' : ''}`}>
        <td className="expand-cell">
          <button
            className="expand-button"
            onClick={() => toggleRow(item.symbol)}
            title={isExpanded ? 'Hide details' : 'Show score details'}
          >
            {isExpanded ? '▼' : '▶'}
          </button>
        </td>
        <td className="symbol-cell">
          <TickerLogo symbol={item.symbol} isCrypto={isCrypto} className="inline-gap" />
          <strong
            className="symbol-clickable"
            onClick={(e) => { e.stopPropagation(); toggleRow(item.symbol); }}
            title={`Expand ${item.symbol}`}
          >{item.symbol}</strong>
          {isCrypto && <span className="crypto-badge">₿</span>}
          {isETF && <span className="etf-badge">ETF</span>}
          {isMutualFund && <span className="fund-badge">FUND</span>}
          <WatchButton symbol={item.symbol} className="portfolio-watch-btn" />
          <CompanyName symbol={item.symbol} crypto={!!isCrypto} className="co-name-cell" />
        </td>
        <td className="data-cell">{item.shares?.toLocaleString()}</td>
        <td className="data-cell">{formatCurrency(item.entry_price)}</td>
        <td className="data-cell">{formatCurrency(item.current_price || score?.current_price)}</td>
        <td className={`pnl-cell ${(score?.pnl_percent || 0) >= 0 ? 'positive' : 'negative'}`}>
          {formatPercent(score?.pnl_percent)}
        </td>
        <td className="data-cell">{formatCurrency(score?.position_value)}</td>
        <td className="group-start-scores">
          {(isCrypto || isETF || isMutualFund) ? (
            <span
              className="score-badge score-badge-na"
              title={
                isCrypto
                  ? "Quality scoring measures company fundamentals (margins, balance sheet, cash flow). Digital assets don't have these, so this score doesn't apply."
                  : isETF
                    ? "Quality scoring measures company fundamentals. ETFs are baskets — quality should be judged at the holdings level, not the wrapper."
                    : "Quality scoring measures company fundamentals. Mutual funds are baskets — quality should be judged at the holdings level, not the wrapper."
              }
            >
              N/A
            </span>
          ) : (
            <span
              className="score-badge"
              style={{
                backgroundColor: getScoreColor(score?.stock_quality_score, 100),
              }}
            >
              {formatScore(score?.stock_quality_score)}
            </span>
          )}
        </td>
        <td>
          <span
            className="score-badge"
            style={{
              backgroundColor: getScoreColor(score?.stock_setup_score, 100),
            }}
          >
            {formatScore(score?.stock_setup_score)}
          </span>
        </td>
        <td>
          <span
            className="score-badge"
            style={{
              backgroundColor: getScoreColor(score?.convex_radar_score, 5),
            }}
          >
            {formatScore(score?.convex_radar_score)}
          </span>
        </td>
        <td className="group-start-conviction">
          <span
            className="conviction-badge"
            style={{
              background: 'transparent',
              border: `2px solid ${bandColor.border}`,
              color: bandColor.text,
            }}
            title={guidance || `Conviction: ${conviction}/100`}
          >
            {formatScore(conviction)}
          </span>
        </td>
        <td className="group-start-action">
          {isCrypto ? (
            <span className={`sentiment-badge sentiment-${cryptoSentiment?.className}`}>
              {cryptoSentiment?.label}
            </span>
          ) : (
            <span className={`action-badge action-${action?.toLowerCase().replace('/', '-')}`}>
              {action}
            </span>
          )}
        </td>
      </tr>

      {isExpanded && (
        <tr className="detail-row">
          <td colSpan="13">
            {guidance && (
              <div className="conviction-guidance">
                <p>{guidance}</p>
              </div>
            )}
            <PortfolioExpandedRow
              score={score}
              selectedTest={selectedTests?.[item.symbol] || null}
              handleTestClick={handleTestClick}
              showEnergyBreakdown={showEnergyBreakdown}
              toggleEnergyBreakdown={toggleEnergyBreakdown}
              getScoreColor={getScoreColor}
              symbol={item.symbol}
              turbulence={turbulence}
              setChartSymbol={setChartSymbol}
            />
          </td>
        </tr>
      )}
    </React.Fragment>
  );
};

export default PortfolioRow;
